import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, UserCheck, UserX, FileText, Star, CalendarClock, TrendingUp, ArrowRight, Clock } from 'lucide-react';
import './RecruitmentDashboard.css';

const statusColors = {
    Applied: { bg: '#eff6ff', color: '#1d4ed8' },
    Shortlisted: { bg: '#f0fdf4', color: '#15803d' },
    'Phone Screen': { bg: '#fefce8', color: '#a16207' },
    Interview: { bg: '#fdf4ff', color: '#7e22ce' },
    Hired: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fef2f2', color: '#991b1b' },
    Scheduled: { bg: '#eff6ff', color: '#1d4ed8' },
    Completed: { bg: '#f0fdf4', color: '#15803d' },
    Cancelled: { bg: '#fef2f2', color: '#991b1b' },
    Pending: { bg: '#fefce8', color: '#a16207' },
};

const StatusBadge = ({ status }) => {
    const s = statusColors[status] || { bg: '#f1f5f9', color: '#475569' };
    return (
        <span className="rc-badge" style={{ background: s.bg, color: s.color }}>
            {status}
        </span>
    );
};

const RecruitmentDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalOpenings: 0,
        totalApplications: 0,
        totalHired: 0,
        totalRejected: 0,
        newApplications: 0,
        shortlisted: 0,
        todaysInterviews: 0,
        jobStats: [],
        recentInterviews: [],
        openJobsList: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchDashboardData(); }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await api.get('/recruitment/dashboard-stats');
            setStats(response.data);
        } catch (error) {
            console.error("Error fetching recruitment stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        { label: 'Open Positions', value: stats.totalOpenings, icon: <Briefcase size={22} />, color: '#6366f1', bg: '#eef2ff', link: '/recruitment/jobs' },
        { label: 'Total Applicants', value: stats.totalApplications, icon: <Users size={22} />, color: '#0ea5e9', bg: '#f0f9ff', link: '/recruitment/candidate-database' },
        { label: 'Hired', value: stats.totalHired, icon: <UserCheck size={22} />, color: '#10b981', bg: '#ecfdf5', link: '/recruitment/candidate-database' },
        { label: 'Rejected', value: stats.totalRejected, icon: <UserX size={22} />, color: '#ef4444', bg: '#fff1f2', link: '/recruitment/candidate-database' },
        { label: 'New Applications', value: stats.newApplications, icon: <FileText size={22} />, color: '#f59e0b', bg: '#fffbeb', link: '/recruitment/job-applications' },
        { label: 'Shortlisted', value: stats.shortlisted, icon: <Star size={22} />, color: '#8b5cf6', bg: '#f5f3ff', link: '/recruitment/job-applications' },
        { label: "Today's Interviews", value: stats.todaysInterviews, icon: <CalendarClock size={22} />, color: '#06b6d4', bg: '#ecfeff', link: '/recruitment/interview-schedule' },
    ];

    if (loading) return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <div className="skeleton skeleton-title" style={{ width: 240, marginBottom: 8 }} />
                    <div className="skeleton skeleton-text" style={{ width: 160 }} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div className="skeleton skeleton-btn" style={{ width: 120 }} />
                    <div className="skeleton skeleton-btn" style={{ width: 145 }} />
                </div>
            </div>

            {/* 7 Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
                {[1,2,3,4,5,6,7].map(i => (
                    <div key={i} style={{ background: 'white', borderRadius: 12, padding: '20px 22px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div className="skeleton skeleton-text" style={{ width: '65%', marginBottom: 10 }} />
                                <div className="skeleton" style={{ width: '40%', height: 32, borderRadius: 6 }} />
                            </div>
                            <div className="skeleton" style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Pipeline Funnel Bar */}
            <div className="dark-card" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
                    <div className="skeleton skeleton-title" style={{ width: 200 }} />
                    <div className="skeleton skeleton-text" style={{ width: 90 }} />
                </div>
                <div className="skeleton" style={{ height: 40, borderRadius: 8, marginBottom: 12 }} />
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 4 }}>
                    {[1,2,3,4,5].map(i => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div className="skeleton" style={{ width: 10, height: 10, borderRadius: '50%' }} />
                            <div className="skeleton skeleton-text" style={{ width: 80 }} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom Grid */}
            <div className="bottom-grid">
                {/* Left: Applications by Job */}
                <div className="dark-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
                        <div className="skeleton skeleton-title" style={{ width: 180 }} />
                        <div className="skeleton skeleton-text" style={{ width: 40 }} />
                    </div>
                    {/* Table header cols */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                        <div className="skeleton skeleton-text" style={{ flex: 2 }} />
                        {[1,2,3,4].map(i => <div key={i} className="skeleton skeleton-text" style={{ flex: 1 }} />)}
                    </div>
                    {/* Rows */}
                    {[1,2,3,4,5,6].map(i => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ flex: 2 }}>
                                <div className="skeleton skeleton-text" style={{ width: '80%', marginBottom: 5 }} />
                                <div className="skeleton skeleton-text" style={{ width: '50%' }} />
                            </div>
                            {[1,2,3,4].map(j => (
                                <div key={j} style={{ flex: 1 }}>
                                    <div className="skeleton" style={{ height: 22, borderRadius: 20, width: 40 }} />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                {/* Right: Today's Interviews + Open Jobs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {/* Today's Interviews */}
                    <div className="dark-card" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
                            <div className="skeleton skeleton-title" style={{ width: 150 }} />
                            <div className="skeleton skeleton-text" style={{ width: 35 }} />
                        </div>
                        {[1,2,3,4].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div className="skeleton skeleton-text" style={{ width: 130, marginBottom: 6 }} />
                                    <div className="skeleton skeleton-text" style={{ width: 110 }} />
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div className="skeleton skeleton-text" style={{ width: 50, marginBottom: 6 }} />
                                    <div className="skeleton" style={{ height: 22, borderRadius: 20, width: 72 }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Open Jobs */}
                    <div className="dark-card" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
                            <div className="skeleton skeleton-title" style={{ width: 90 }} />
                            <div className="skeleton skeleton-text" style={{ width: 35 }} />
                        </div>
                        {[1,2,3,4].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div className="skeleton skeleton-text" style={{ width: 120, marginBottom: 6 }} />
                                    <div className="skeleton skeleton-text" style={{ width: 95 }} />
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div className="skeleton skeleton-text" style={{ width: 65, marginBottom: 6 }} />
                                    <div className="skeleton" style={{ height: 22, borderRadius: 20, width: 44 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <h2>Recruitment Dashboard</h2>
                    <div className="breadcrumb"><span className="bc-parent">Home</span><span className="bc-sep"> • </span><span className="bc-parent">Recruit</span><span className="bc-sep"> • </span><span className="bc-current">Dashboard</span></div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => navigate('/recruitment/jobs')} className="rc-btn rc-btn-primary">
                        <Briefcase size={15} /> Post a Job
                    </button>
                    <button onClick={() => navigate('/recruitment/candidate-database')} className="rc-btn rc-btn-secondary">
                        <Users size={15} /> Add Candidate
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="rc-stat-grid">
                {statCards.map((card, i) => (
                    <div key={i} className="rc-stat-card" onClick={() => navigate(card.link)}>
                        <div>
                            <div className="rc-stat-label">{card.label}</div>
                            <div className="rc-stat-value">{card.value}</div>
                        </div>
                        <div className="rc-stat-icon" style={{ background: card.bg, color: card.color }}>{card.icon}</div>
                    </div>
                ))}
            </div>

            {/* Pipeline Funnel Bar */}
            <div className="dark-card" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--rc-border)' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TrendingUp size={18} style={{ color: 'var(--rc-indigo)' }} /> Hiring Pipeline Overview
                    </div>
                    <button onClick={() => navigate('/recruitment/job-applications')} style={{ background: 'none', border: 'none', color: 'var(--rc-indigo)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>View Pipeline <ArrowRight size={14} /></button>
                </div>
                <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', height: 40 }}>
                    {[
                        { label: 'Applied', val: stats.newApplications, color: '#3b82f6' },
                        { label: 'Shortlisted', val: stats.shortlisted, color: '#8b5cf6' },
                        { label: 'Interview', val: stats.todaysInterviews, color: '#f59e0b' },
                        { label: 'Hired', val: stats.totalHired, color: '#10b981' },
                        { label: 'Rejected', val: stats.totalRejected, color: '#ef4444' },
                    ].map((s, i) => {
                        const total = (stats.newApplications || 0) + (stats.shortlisted || 0) + (stats.todaysInterviews || 0) + (stats.totalHired || 0) + (stats.totalRejected || 0) || 1;
                        const pct = Math.max((s.val / total) * 100, 2);
                        return (
                            <div key={i} title={`${s.label}: ${s.val}`} style={{ background: s.color, flex: pct, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 700, transition: 'flex 0.5s ease', minWidth: 0, overflow: 'hidden' }}>
                                {pct > 8 ? s.label : ''}
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Applied', val: stats.newApplications, color: '#3b82f6' },
                        { label: 'Shortlisted', val: stats.shortlisted, color: '#8b5cf6' },
                        { label: 'Interview', val: stats.todaysInterviews, color: '#f59e0b' },
                        { label: 'Hired', val: stats.totalHired, color: '#10b981' },
                        { label: 'Rejected', val: stats.totalRejected, color: '#ef4444' },
                    ].map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                            <span>{s.label}: <b style={{ color: '#1e293b' }}>{s.val}</b></span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Applications per Job & Today's Interviews */}
            <div className="bottom-grid">
                {/* Applications per Job */}
                <div className="dark-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--rc-border)' }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#16a34a' }}>Applications by Job</div>
                        <button onClick={() => navigate('/recruitment/job-applications')} style={{ background: 'none', border: 'none', color: 'var(--rc-indigo)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>All <ArrowRight size={13} /></button>
                    </div>
                    <table className="dark-table">
                        <thead>
                            <tr>
                                <th>Job</th>
                                <th>Applied</th>
                                <th>Interview</th>
                                <th>Hired</th>
                                <th>Rejected</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.jobStats.length > 0 ? stats.jobStats.slice(0, 6).map((job, i) => (
                                <tr key={i}>
                                    <td className="job-title-cell">
                                        <div>{job.jobTitle}</div>
                                        <div className="job-sub-text">{job.totalApplications} total</div>
                                    </td>
                                    <td><span className="rc-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>{job.applied}</span></td>
                                    <td><span className="rc-badge" style={{ background: '#fdf4ff', color: '#7e22ce' }}>{job.interview}</span></td>
                                    <td><span className="rc-badge" style={{ background: '#dcfce7', color: '#166534' }}>{job.hired}</span></td>
                                    <td><span className="rc-badge" style={{ background: '#fef2f2', color: '#991b1b' }}>{job.rejected}</span></td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No application data yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Right column: Today's Interviews + Open Jobs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {/* Today's Interviews */}
                    <div className="dark-card" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--rc-border)' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 7 }}>
                                <Clock size={16} style={{ color: '#0ea5e9' }} /> Today's Interviews
                            </div>
                            <button onClick={() => navigate('/recruitment/interview-schedule')} style={{ background: 'none', border: 'none', color: 'var(--rc-indigo)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>All <ArrowRight size={13} /></button>
                        </div>
                        {stats.recentInterviews.length > 0 ? stats.recentInterviews.map((iv, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < stats.recentInterviews.length - 1 ? '1px solid var(--rc-border-light)' : 'none' }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--rc-text)', fontSize: 13 }}>{iv.candidateId?.firstName} {iv.candidateId?.lastName}</div>
                                    <div style={{ fontSize: 12, color: 'var(--rc-text-2)' }}>{iv.jobId?.title} · {iv.type}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 12, color: 'var(--rc-text-2)' }}>{new Date(iv.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                    <StatusBadge status={iv.status} />
                                </div>
                            </div>
                        )) : (
                            <div className="rc-empty">No interviews scheduled for today</div>
                        )}
                    </div>

                    {/* Open Jobs */}
                    <div className="dark-card" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--rc-border)' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#16a34a' }}>Open Jobs</div>
                            <button onClick={() => navigate('/recruitment/jobs')} style={{ background: 'none', border: 'none', color: 'var(--rc-indigo)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>All <ArrowRight size={13} /></button>
                        </div>
                        {stats.openJobsList.length > 0 ? stats.openJobsList.map((job, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < stats.openJobsList.length - 1 ? '1px solid var(--rc-border-light)' : 'none' }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--rc-text)', fontSize: 13 }}>{job.title}</div>
                                    <div style={{ fontSize: 12, color: 'var(--rc-text-2)' }}>{job.department} · {job.type}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, color: 'var(--rc-text-3)' }}>{job.deadline ? `Due ${new Date(job.deadline).toLocaleDateString()}` : 'No deadline'}</div>
                                    <span className="rc-badge" style={{ background: '#ecfdf5', color: '#15803d' }}>Open</span>
                                </div>
                            </div>
                        )) : (
                            <div className="rc-empty">No open jobs found</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecruitmentDashboard;
